import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { ComboBoxComponent } from '@progress/kendo-angular-dropdowns';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  Observable,
  Subject,
  Subscription,
  tap,
} from 'rxjs';
import { KendoModule } from '../../kendo/kendo.module';
import {
  AutocompleteQueryService,
  NomenclatureType,
  SearchOperation,
} from './autocomplete-query.service';

@Component({
  selector: 'app-autocomplete',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  templateUrl: './autocomplete.component.html',
  styleUrls: ['./autocomplete.component.scss'],
  providers: [
    AutocompleteQueryService,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutocompleteComponent),
      multi: true,
    },
  ],
  imports: [ReactiveFormsModule, KendoModule, CommonModule],
})
export class AutocompleteComponent
  implements AfterViewInit, OnDestroy, OnChanges, ControlValueAccessor, OnInit
{
  @Input() searchProp: NomenclatureType | keyof typeof NomenclatureType =
    NomenclatureType.Cities;
  @Input() label: string = 'label';
  @Input() valueName: string = 'name';
  @Input() parentSearchProp: NomenclatureType | undefined;
  @Input() parentSearchPropName: 'Parent' | 'Contact' | 'Type' = 'Parent';
  @Input() parentSearchPropValue: number | string | undefined = undefined;
  @Input() searchInitialValue: boolean = false;
  @Input() showCode = false;
  @Input() isComplexType = false;
  @Input() isExact = false;

  @Input() complexTypeValueNormalizer?: (data: any) => string;

  @ViewChild('autocomplete') autocompleteComp!: ComboBoxComponent;

  public data!: Observable<any>;

  public control: FormControl;

  private sub: Subscription;
  private filterValues: Subject<string> = new Subject<string>();

  constructor(public query: AutocompleteQueryService) {
    this.control = new FormControl('');
    this.sub = this.control.valueChanges.subscribe((data) => {
      if (!data) {
        this.onChange(null);
        return;
      }
      this.onChange(data);
    });

    this.sub.add(
      this.filterValues
        .pipe(
          debounceTime(400),
          distinctUntilChanged(),
          tap((val) =>
            this.query.search(
              val,
              this.searchProp,
              this.parentSearchPropName,
              this.parentSearchPropValue,
              this.showCode,
              this.isExact,
              this.isComplexType,
              this.complexTypeValueNormalizer
            )
          )
        )
        .subscribe()
    );
  }

  onChange = (data: any) => {};
  onTouch = () => {};

  ngOnInit() {
    this.query
      .manualSearch(
        '',
        this.searchProp,
        SearchOperation.LIKE,
        this.parentSearchPropName,
        this.parentSearchPropValue,
        this.showCode,
        this.isComplexType,
        this.complexTypeValueNormalizer
      )
      .subscribe((res) => {});
  }

  writeValue(obj: any): void {
    console.log(obj);
    this.control.patchValue(obj);
    if (this.searchInitialValue && this.control.value?.name) {
      this.query
        .manualSearch(
          this.control.value.name,
          this.searchProp,
          SearchOperation.EQUAL,
          'Parent',
          undefined,
          this.showCode,
          this.isComplexType,
          this.complexTypeValueNormalizer
        )
        .subscribe((res) => (this.autocompleteComp.value = res));
    }
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['parentSearchPropValue'] &&
      !changes['parentSearchPropValue'].isFirstChange()
    ) {
      //Clean form control value if parent control changes
      this.autocompleteComp.value = undefined;

      this.query.search(
        '',
        this.searchProp,
        this.parentSearchPropName,
        this.parentSearchPropValue,
        this.showCode,
        false,
        this.isComplexType,
        this.complexTypeValueNormalizer
      );
    }
  }

  valueNormalizer = (text: Observable<string>) =>
    text.pipe(
      map((content: string) => {
        return {
          id: null,
          name: content,
        };
      })
    );

  ngAfterViewInit(): void {}

  handleFilter(value: string) {
    // this.query.search(value, this.searchProp);
    if (!!value) this.filterValues.next(value);
  }

  ngOnDestroy(): void {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }
}
